<template>
  <div class="content" id="order-edit">
    <h1>{{ pageTitle }}</h1>
    <div class="content-body two-columns form-wrapper">
      <div class="left">
        <div class="block">
          <Alert v-show="!orderFormValidated" type="warning" closable>{{$t('order.order_validate_before_send_to_erp')}}</Alert>
          <div class="text-align-right" v-if="!hasErpOrder">
            <Button size="small" style="margin-left: 5px;" type="warning" @click="handleSend2ERPClicked" :disabled="!orderFormValidated" v-if="showSend2ERPButton" :loading="sendingOrder">{{ $t('order.send_order_to_erp') }}</Button>
          </div>
          <div class="warning" v-if="hasErpOrder">
            <Alert type="warning" show-icon>{{ $t('order.order_already_in_erp_msg') }}</Alert>
          </div>
          <div class="warning" v-if="hasQBOEstimate">
            <Icon size="22" type="md-information-circle" />{{ $t('order.order_already_in_qbo_msg') }} : {{this.orderData.qbo_estimate_synced.updated_at}}
          </div>
          <OrderForm ref="orderForm" v-if="orderData" :order-data="orderData" :loading="loading" :validated.sync="orderFormValidated" :disabled="hasErpOrder"></OrderForm>
        </div>
        <div class="block margin-top-20">
          <h2>{{ $t('order.order_items') }}</h2>
          <OrderItems v-if="orderData && orderData.id"
                      :order-id="orderData.id"
                      :currency="orderData.currency"
                      :order="orderData"
                      :disabled="hasErpOrder"
                      @on-order-item-updated="handleOrderItemUpdated">
          </OrderItems>

          <div v-if="orderData.id">
            <h2>{{ $t('order.order_change_records') }}</h2>
            <OrderChangeRecords :order-id="orderData.id" :currency="orderData.currency" @on-clicked="handleChangeRecordClicked"></OrderChangeRecords>
            <div class="text-align-right margin-top-20">
              <Button icon="ios-add" style="margin-left: 5px;" type="primary" @click="handleChangeRecordNewClicked">{{ $t('order.add_order_change_fee') }}</Button>
            </div>
          </div>
          <div class="block text-align-right" v-if="orderData">
            <h3>{{ $t('order.accumulated_amount') }}</h3>
            <p style="font-weight: bold; font-size: 200%;">
              {{ intlCurrencyNumber(orderData.currency, orderData.amount, 4, 4) }}
            </p>
          </div>
        </div>
      </div>
      <div class="right">
        <div class="block" v-if="orderData.account_id">
          <CustomerInfoSimpleBox :customer-id="orderData.account_id"></CustomerInfoSimpleBox>
        </div>
        <div class="block margin-top-20">
          <h3>ERP {{ $t('menu.order.detail') }}</h3>
          <ErpOrderInfo :order-id="orderData.id" @on-erp-order-loaded="handleErpOrderLoaded"></ErpOrderInfo>
        </div>
        <div class="block margin-top-20">
          <h3>ERP {{ $t('order.prepays') }}</h3>
          <ErpOrderPrepays v-if="orderData.id" :order-id="orderData.id"></ErpOrderPrepays>
        </div>
        <div class="block margin-top-20">
          <h3>QuickBooks (sales order)</h3>
          <QuickBooksEstimateLink :order-data="orderData"></QuickBooksEstimateLink>
        </div>
      </div>
    </div>
    <Modal :title="$t('order.order_change_modal_title')" v-model="showAdditionalFeeForm" width="1000" footer-hide>
      <OrderAdditionalFeeForm ref="orderAdditionalFeeFormModal" :is-new="isAdditionalFeeNew" :record-data="orderAddtionalFeeRecord"></OrderAdditionalFeeForm>
    </Modal>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { OrderForm, OrderAdditionalFeeForm } from './forms'
import OrderItems from './order-items'
import { CustomerInfoSimpleBox } from '@/view/customer/components'
import { OrderChangeRecords, QuickBooksEstimateLink, ErpOrderInfo, ErpOrderPrepays } from './widgets'
import { intlCurrencyNumber } from '@/libs/util'
export default {
  name: 'Order',
  components: {
    OrderForm,
    OrderAdditionalFeeForm,
    OrderItems,
    CustomerInfoSimpleBox,
    OrderChangeRecords,
    QuickBooksEstimateLink,
    ErpOrderInfo,
    ErpOrderPrepays
  },
  computed: {
    // ...mapGetters(['orderData']),
    pageTitle () {
      let title = this.$t('menu.order.detail') + ' - ' + this.orderData?.description
      document.title = title + ' - ' + this.orderData?.order_number
      return title
    },
    showSend2ERPButton () {
      return this.orderData && this.orderData.id && !this.orderData.erp_order
    },
    showSend2QBOButton () {
      return this.orderData && this.orderData.id && this.orderData.erp_order && this.orderData.accounting_company === 'NexGroup Inc.'
    },
    hasErpOrder () {
      // return this.orderData && (!!this.orderData.erp_order || this.erpOrderIsLoaded)
      return false
    },
    hasQBOEstimate () {
      return this.orderData && !!this.orderData.qbo_estimate_synced
    }
  },
  data () {
    return {
      sendingOrder: false,
      loading: false,
      orderFormValidated: true,
      showAdditionalFeeForm: false,
      isAdditionalFeeNew: false,
      orderData: {},
      orderAddtionalFeeRecord: {
        order_number: '',
        currency: 'USD',
        exchange_rate: 0
      },
      erpOrderIsLoaded: false
    }
  },
  mounted () {
    this.loadOrder()
  },
  methods: {
    ...mapActions(['getOrder', 'sendOrderToERP']),
    intlCurrencyNumber,
    loadOrder () {
      this.loading = true
      this.getOrder(this.$route.params.id).then((res) => {
        this.orderData = res.data
        this.$set(this.orderAddtionalFeeRecord, 'order_number', this.orderData.order_number)
        this.$set(this.orderAddtionalFeeRecord, 'currency', this.orderData.currency)
        this.$set(this.orderAddtionalFeeRecord, 'exchange_rate', this.orderData.exchange_rate)
        this.$set(this.orderAddtionalFeeRecord, 'order_id', this.orderData.id)
      }).finally(() => {
        this.loading = false
        this.$nextTick(() => {
          this.$refs['orderForm'].handleValidateForm()
        })
      })
    },
    handleSend2ERPClicked () {
      this.sendingOrder = true

      this.sendOrderToERP(this.orderData.id)
        .then(resp => {
          this.orderData.erp_order = resp.data
          this.$Message.success('Order has been successfully created in ERP.')
          this.$router.go()
        })
        .catch(e => {
          this.$Message.error('Sorry, there is an error ocurred!')
        })
        .finally(() => {
          this.sendingOrder = false
        })
    },
    handleChangeRecordClicked (record) {
      this.showAdditionalFeeForm = true
      this.orderAddtionalFeeRecord = record
      this.isAdditionalFeeNew = false
    },
    handleChangeRecordNewClicked () {
      this.showAdditionalFeeForm = true
      this.isAdditionalFeeNew = true
      this.orderAddtionalFeeRecord = {
        order_number: this.orderData.order_number,
        currency: this.orderData.currency,
        exchange_rate: this.orderData.exchange_rate,
        order_id: this.orderData.id
      }
    },
    handleOrderItemUpdated () {
      this.getOrder(this.$route.params.id).then((resp) => {
        this.orderData = resp.data
      })
    },
    handleErpOrderLoaded (erpOrder) {
      this.erpOrderIsLoaded = !!erpOrder
    }
  }
}

</script>
